import React, { useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Tooltip, Button, Select, MenuItem,
} from '@mui/material';
import { Users, Check } from 'Utils/lucideIcons';
import {
  AnimatedCopy, AnimatedUserRoundPlus, AnimatedTrash, AnimatedX,
} from 'Components/reactbits/AnimatedIcons';
import { V2ThemeProvider } from 'Pages/private/v2/_shared/V2Layout';
import PillSearch from 'Components/PillSearch';

const DOT = { all: '#6b7280', active: '#16a34a', pending: '#d97706' };

// Lucide chevron-down for the dropdown; MUI rotates it on open.
const ChevronDownIcon = (props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <svg {...props} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/**
 * The dashboard's Viewers panel, extracted for reuse: a borderless white card
 * with a toolbar (title/count on the left; a viewer-type dropdown — colored dot
 * + label + count — grouped next to Copy link + Invite on the right) above a
 * table. Icons are the framer-motion animated lucide set. No internal scroll —
 * the table grows and the page scrolls.
 */
export const ViewersTable = ({
  rows = [],
  filter = 'all',
  onFilterChange,
  activeCount = 0,
  pendingCount = 0,
  copied = false,
  onCopy,
  onInvite,
  onCancelInvite,
  onRemove,
  emptyText = 'No viewers yet',
}) => {
  const filters = [
    { value: 'all', label: 'All', count: activeCount + pendingCount },
    { value: 'active', label: 'Active', count: activeCount },
    { value: 'pending', label: 'Pending', count: pendingCount },
  ];
  const dot = (color) => (
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
  );

  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const shown = q
    ? rows.filter((r) => (
      `${r.firstname || ''} ${r.lastname || ''}`.toLowerCase().includes(q)
      || (r.email || '').toLowerCase().includes(q)
    ))
    : rows;

  return (
    <V2ThemeProvider withBaseline={false}>
      <Box
        sx={{
          bgcolor: '#ffffff',
          border: '1px solid #e6e8ec',
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(15,17,28,0.06)',
          p: { xs: 1.5, md: 2 },
          boxSizing: 'border-box',
        }}
      >
        {/* Toolbar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, mb: 1.5 }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#15171c' }}>Viewers</Typography>
            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
              {`${activeCount} active · ${pendingCount} pending`}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} useFlexGap flexWrap="wrap">
            <PillSearch value={search} onChange={setSearch} placeholder="Search viewers" minWidth={180} />
            <Select
              value={filter}
              onChange={(e) => onFilterChange?.(e.target.value)}
              size="small"
              IconComponent={ChevronDownIcon}
              renderValue={(val) => {
                const f = filters.find((x) => x.value === val) || filters[0];
                return (
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    {dot(DOT[val])}
                    <span>{f.label}</span>
                    <Box component="span" sx={{ color: '#6b7280', fontWeight: 600 }}>{f.count}</Box>
                  </Stack>
                );
              }}
              sx={{ minWidth: 96, borderRadius: '10px', fontSize: 14, fontWeight: 400, bgcolor: '#fff' }}
            >
              {filters.map((f) => (
                <MenuItem key={f.value} value={f.value} sx={{ fontSize: 14, fontWeight: 400 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
                    {dot(DOT[f.value])}
                    <Box sx={{ flex: 1 }}>{f.label}</Box>
                    <Box sx={{ color: '#6b7280', fontWeight: 600, fontSize: 13 }}>{f.count}</Box>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
            {/* Copy link + Invite joined into one control. */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'stretch',
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid #d0d5dd',
                flexShrink: 0,
              }}
            >
              <Button
                size="small"
                onClick={onCopy}
                startIcon={copied ? <Check size={16} /> : <AnimatedCopy size={16} />}
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  borderRadius: 0,
                  fontWeight: 500,
                  color: '#15171c',
                  bgcolor: '#fff',
                  borderRight: '1px solid #e6e8ec',
                  px: 1.5,
                  '&:hover': { bgcolor: '#f4f5f7' },
                }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={onInvite}
                startIcon={<AnimatedUserRoundPlus size={16} />}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderRadius: 0, fontWeight: 500, px: 1.75 }}
              >
                Invite
              </Button>
            </Box>
          </Stack>
        </Stack>

        {!shown.length ? (
          <Stack spacing={1} sx={{ alignItems: 'center', py: 6, color: '#6b7280' }}>
            <Users size={28} />
            <Typography variant="body2">{q ? `No viewers match “${search.trim()}”` : emptyText}</Typography>
            {onInvite && (
              <Button
                onClick={onInvite}
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AnimatedUserRoundPlus size={16} />}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Invite a viewer
              </Button>
            )}
          </Stack>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box
                  component="tr"
                  sx={{ '& th': { textAlign: 'left', py: 1, px: 1, fontSize: 12, fontWeight: 700, color: '#9aa1ab', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' } }}
                >
                  <Box component="th">Name</Box>
                  <Box component="th">Email</Box>
                  <Box component="th">Status</Box>
                  <Box component="th" sx={{ textAlign: 'right !important' }}>Action</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {shown.map((row) => {
                  const name = `${row.firstname || ''} ${row.lastname || ''}`.trim()
                    || row.email?.split('@')[0] || '—';
                  const isPending = row.status === 'pending';
                  return (
                    <Box
                      component="tr"
                      key={row.id || row.email}
                      sx={{ '& td': { py: 1.25, px: 1, fontSize: 14, verticalAlign: 'middle' }, '&:hover td': { bgcolor: '#f8f9fb' } }}
                    >
                      <Box component="td">
                        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#eef1f6', color: '#4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, textTransform: 'uppercase' }}>
                            {name.charAt(0)}
                          </Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#15171c' }} noWrap>{name}</Typography>
                        </Stack>
                      </Box>
                      <Box component="td">
                        <Typography variant="body2" sx={{ color: '#6b7280' }} noWrap>{row.email}</Typography>
                      </Box>
                      <Box component="td">
                        <Chip
                          size="small"
                          label={isPending ? 'Pending' : 'Active'}
                          color={isPending ? 'warning' : 'success'}
                          variant="outlined"
                          sx={{ fontWeight: 600, height: 22 }}
                        />
                      </Box>
                      <Box component="td" sx={{ textAlign: 'right' }}>
                        <Tooltip title={isPending ? 'Cancel invite' : 'Remove viewer'}>
                          <IconButton
                            size="small"
                            onClick={() => (isPending ? onCancelInvite?.(row.email) : onRemove?.(row.id, name))}
                            sx={{ color: isPending ? '#6b7280' : '#d3302f' }}
                          >
                            {isPending ? <AnimatedX size={17} /> : <AnimatedTrash size={17} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </V2ThemeProvider>
  );
};

export default ViewersTable;
